import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

const startOfWeek = (d) => {
    const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    x.setDate(x.getDate() - x.getDay());
    return x;
};

const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const EventChip = ({ e, onSelect, showTime = true }) => (
    <button
        type="button"
        onClick={() => onSelect(e)}
        data-testid={`calendar-event-${e.id}`}
        title={`${e.title}${e.time ? ` · ${e.time}` : ""}`}
        className="mt-1 block w-full truncate border border-ink bg-mustard px-1 text-left font-display text-[10px] tracking-wide text-ink transition-transform duration-100 hover:-translate-y-0.5 hover:bg-orange hover:text-cream"
    >
        {showTime && e.time ? <span className="text-brick">{e.time.split("–")[0].trim()} · </span> : null}
        {e.all_day ? <span className="text-brick">ALL DAY · </span> : null}
        {e.title}
    </button>
);

const CalendarBoard = ({ events = [], onSelect }) => {
    const [view, setViewState] = useState(() => localStorage.getItem("pcch-cal-view") || "month");
    const [cursor, setCursor] = useState(() => new Date());
    const today = new Date();

    const parsed = useMemo(
        () => events.map((e) => ({ ...e, _s: new Date(e.start) })).filter((e) => !isNaN(e._s)),
        [events],
    );

    const setView = (v) => {
        setViewState(v);
        localStorage.setItem("pcch-cal-view", v);
    };

    const shift = (n) => {
        const d = new Date(cursor);
        if (view === "month") {
            d.setDate(1);
            d.setMonth(d.getMonth() + n);
        } else {
            d.setDate(d.getDate() + 7 * n);
        }
        setCursor(d);
    };

    const eventsOn = (day) => parsed.filter((e) => sameDay(e._s, day));

    const label = () => {
        if (view === "month") return `${MONTHS[cursor.getMonth()].toUpperCase()} ${cursor.getFullYear()}`;
        const ws = startOfWeek(cursor);
        const we = new Date(ws);
        we.setDate(we.getDate() + 6);
        const fmt = (d) => `${MONTHS[d.getMonth()].slice(0, 3).toUpperCase()} ${d.getDate()}`;
        return `${fmt(ws)} – ${fmt(we)}, ${we.getFullYear()}`;
    };

    const weekDays = useMemo(() => {
        const ws = startOfWeek(cursor);
        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date(ws);
            d.setDate(d.getDate() + i);
            return d;
        });
    }, [cursor]);

    const monthCells = useMemo(() => {
        const year = cursor.getFullYear();
        const month = cursor.getMonth();
        const firstDow = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        return [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1))];
    }, [cursor]);

    const navBtn =
        "border-2 border-ink bg-cream p-1.5 hard-shadow transition-transform duration-150 hover:-translate-y-0.5";

    return (
        <div data-testid="calendar-board" className="border-2 border-ink bg-cream hard-shadow-lg">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b-2 border-ink bg-mustard px-4 py-3">
                <div className="flex items-center gap-2">
                    <button data-testid="calendar-prev" onClick={() => shift(-1)} aria-label={view === "month" ? "Previous month" : "Previous week"} className={navBtn}>
                        <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                        data-testid="calendar-today"
                        onClick={() => setCursor(new Date())}
                        className="border-2 border-ink bg-cream px-3 py-1.5 font-display text-sm tracking-[0.15em] text-ink hard-shadow transition-transform duration-150 hover:-translate-y-0.5"
                    >
                        TODAY
                    </button>
                    <button data-testid="calendar-next" onClick={() => shift(1)} aria-label={view === "month" ? "Next month" : "Next week"} className={navBtn}>
                        <ChevronRight className="h-5 w-5" />
                    </button>
                </div>
                <p data-testid="calendar-label" className="font-display text-2xl tracking-[0.15em] text-ink sm:text-3xl">{label()}</p>
                <div className="flex border-2 border-ink bg-paper hard-shadow" role="tablist" aria-label="Calendar view">
                    {["week", "month"].map((v) => (
                        <button
                            key={v}
                            role="tab"
                            aria-selected={view === v}
                            data-testid={`calendar-view-${v}`}
                            onClick={() => setView(v)}
                            className={`px-4 py-1.5 font-display text-sm tracking-[0.2em] transition-colors duration-150 ${
                                view === v ? "bg-forest text-cream" : "text-ink/60 hover:text-ink"
                            }`}
                        >
                            {v.toUpperCase()}
                        </button>
                    ))}
                </div>
            </div>

            {view === "month" ? (
                <div className="grid grid-cols-7">
                    {DOW.map((d) => (
                        <div key={d} className="border-b-2 border-ink bg-paper px-1 py-2 text-center font-display text-[10px] tracking-[0.15em] text-ink/60 sm:text-xs">
                            {d}
                        </div>
                    ))}
                    {monthCells.map((day, i) =>
                        day === null ? (
                            <div key={`blank-${i}`} className="min-h-[56px] border-t border-ink/10 bg-paper/50 sm:min-h-[96px]" />
                        ) : (
                            <div
                                key={day.toISOString()}
                                data-testid={`calendar-day-${day.getFullYear()}-${day.getMonth() + 1}-${day.getDate()}`}
                                className={`min-h-[56px] border-t border-ink/10 p-1.5 sm:min-h-[96px] ${sameDay(day, today) ? "bg-mustard/30" : ""}`}
                            >
                                <span className={`inline-block px-1 font-display text-sm ${sameDay(day, today) ? "border-2 border-ink bg-mustard" : "text-ink/70"}`}>
                                    {day.getDate()}
                                </span>
                                {eventsOn(day).map((e) => (
                                    <EventChip key={e.id} e={e} onSelect={onSelect} showTime={false} />
                                ))}
                            </div>
                        ),
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-7">
                    {weekDays.map((day) => (
                        <div
                            key={day.toISOString()}
                            data-testid={`calendar-weekday-${day.getFullYear()}-${day.getMonth() + 1}-${day.getDate()}`}
                            className={`border-b border-ink/10 p-2 sm:min-h-[220px] sm:border-b-0 sm:border-l sm:first:border-l-0 ${sameDay(day, today) ? "bg-mustard/30" : ""}`}
                        >
                            <p className="flex items-baseline gap-2 sm:flex-col sm:gap-0">
                                <span className="font-display text-[10px] tracking-[0.15em] text-ink/60">{DOW[day.getDay()]}</span>
                                <span className={`inline-block px-1 font-display text-lg ${sameDay(day, today) ? "border-2 border-ink bg-mustard" : "text-ink"}`}>
                                    {day.getDate()}
                                </span>
                            </p>
                            {eventsOn(day).length === 0 ? (
                                <p className="mt-1 font-hand text-base text-ink/30 sm:mt-3">—</p>
                            ) : (
                                eventsOn(day).map((e) => <EventChip key={e.id} e={e} onSelect={onSelect} />)
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CalendarBoard;
